import {
  BLOCKS,
  INLINES,
  type Block,
  type Document,
  type Inline,
  type Node as ContentfulNode,
  type Text,
  type TopLevelBlock,
} from '@contentful/rich-text-types';
import { FULL_SHORTCODE_INLINE_CONTEXT_KEYS, RICH_TEXT_ASSET_LINK_KEYS } from 'libs/contentful/rest/adapter/constants';
import { getTypenameForContentTypeId, isLink } from 'libs/contentful/rest/adapter/type-guards';
import { adaptAsset } from 'libs/contentful/rest/adapter/transforms';
import type { AdaptRichTextContext } from 'libs/contentful/rest/adapter/types';
import type { ContentfulAsset, DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import { InlineTextStyle } from 'types/constants';

/**
 * Node types whose resolved `data.target` is stripped to `{ sys: { id } }` so the
 * emitted `json` is always JSON-serializable and the heavy/cyclic resolved entry
 * never crosses the RSC boundary. Five types: the four the adapter strips plus
 * `asset-hyperlink` (a converter addition for RSC-serialization safety).
 */
const TARGET_NODE_TYPES = new Set<string>([
  INLINES.EMBEDDED_ENTRY,
  BLOCKS.EMBEDDED_ENTRY,
  INLINES.ENTRY_HYPERLINK,
  BLOCKS.EMBEDDED_ASSET,
  INLINES.ASSET_HYPERLINK,
]);

const INLINE_NODE_TYPES = new Set<string>(Object.values(INLINES));

/**
 * Max recursion depth for nested rich-text documents (Disclosure.copy / Modal.copy).
 * Targets are stripped at every depth so the result is always JSON-serializable even
 * when the resolved graph is circular.
 */
const MAX_NESTED_DEPTH = 3;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getSysId(value: unknown): string | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const sys = value.sys;
  if (!isRecord(sys)) {
    return undefined;
  }

  return typeof sys.id === 'string' ? sys.id : undefined;
}

/**
 * Resolved-entry guard (toMediaAsset-style): a `fields` record, a string `sys.id`,
 * and a string `sys.contentType.sys.id`. Link stubs fail this guard (no `fields`),
 * so they are omitted from `links` - matching the legacy includesMap-miss behavior.
 */
interface ResolvedEntry {
  fields: Record<string, unknown>;
  sys: { id: string; contentType: { sys: { id: string } } };
}

function isResolvedEntry(value: unknown): value is ResolvedEntry {
  if (!isRecord(value)) {
    return false;
  }

  const fields = value.fields;
  if (!isRecord(fields)) {
    return false;
  }

  const sys = value.sys;
  if (!isRecord(sys)) {
    return false;
  }

  if (typeof sys.id !== 'string') {
    return false;
  }

  const contentType = sys.contentType;
  if (!isRecord(contentType)) {
    return false;
  }

  const contentTypeSys = contentType.sys;
  return isRecord(contentTypeSys) && typeof contentTypeSys.id === 'string';
}

interface ResolvedAsset {
  fields: Record<string, unknown>;
  sys: { id: string };
}

function isResolvedAsset(value: unknown): value is ResolvedAsset {
  return isRecord(value) && isRecord(value.fields) && typeof getSysId(value) === 'string';
}

function shapeAsset(value: ResolvedAsset): ContentfulAsset | undefined {
  const asset = adaptAsset(value);
  const sys = asset.sys;
  if (!isRecord(sys) || typeof sys.id !== 'string') {
    return undefined;
  }

  return {
    sys: { id: sys.id },
    url: typeof asset.url === 'string' ? asset.url : '',
    title: typeof asset.title === 'string' ? asset.title : '',
    description: typeof asset.description === 'string' ? asset.description : '',
    contentType: typeof asset.contentType === 'string' ? asset.contentType : '',
    width: typeof asset.width === 'number' ? asset.width : 0,
    height: typeof asset.height === 'number' ? asset.height : 0,
  };
}

/**
 * Stricter than the existing `isDocument`: requires `nodeType === 'document'` AND a
 * `content` array, so `{ json }` wrapper objects return `false`.
 */
export function isRichTextDocument(value: unknown): value is Document {
  return isRecord(value) && value.nodeType === 'document' && Array.isArray(value.content);
}

function isStrippedTarget(value: unknown, id: string): boolean {
  if (!isRecord(value) || Object.keys(value).length !== 1) {
    return false;
  }

  const sys = value.sys;
  return isRecord(sys) && Object.keys(sys).length === 1 && sys.id === id;
}

function stripTargetFromNodeData(nodeType: string, data: Record<string, unknown>): Record<string, unknown> {
  if (!TARGET_NODE_TYPES.has(nodeType)) {
    return data;
  }

  const targetId = getSysId(data.target);
  if (typeof targetId !== 'string' || isStrippedTarget(data.target, targetId)) {
    return data;
  }

  return {
    ...data,
    target: {
      sys: {
        id: targetId,
      },
    },
  };
}

function isTextNode(node: Block | Inline | Text): node is Text {
  return node.nodeType === 'text';
}

function isInlineNode(node: Block | Inline): node is Inline {
  return INLINE_NODE_TYPES.has(node.nodeType);
}

function stripInlineTargets(node: Inline): Inline {
  const nextData = stripTargetFromNodeData(node.nodeType, node.data);
  let contentChanged = false;

  const nextContent = node.content.map(child => {
    if (isTextNode(child)) {
      return child;
    }

    const strippedChild = stripInlineTargets(child);
    if (strippedChild !== child) {
      contentChanged = true;
    }

    return strippedChild;
  });

  const dataChanged = nextData !== node.data;
  if (!dataChanged && !contentChanged) {
    return node;
  }

  return {
    ...node,
    data: nextData,
    content: contentChanged ? nextContent : node.content,
  };
}

function stripBlockTargets(node: Block): Block {
  const nextData = stripTargetFromNodeData(node.nodeType, node.data);
  let contentChanged = false;

  const nextContent = node.content.map(child => {
    if (isTextNode(child)) {
      return child;
    }

    const strippedChild = isInlineNode(child) ? stripInlineTargets(child) : stripBlockTargets(child);
    if (strippedChild !== child) {
      contentChanged = true;
    }

    return strippedChild;
  });

  const dataChanged = nextData !== node.data;
  if (!dataChanged && !contentChanged) {
    return node;
  }

  return {
    ...node,
    data: nextData,
    content: contentChanged ? nextContent : node.content,
  };
}

function stripTopLevelBlockTargets(node: TopLevelBlock): TopLevelBlock {
  const nextData = stripTargetFromNodeData(node.nodeType, node.data);
  let contentChanged = false;

  const nextContent = node.content.map(child => {
    if (isTextNode(child)) {
      return child;
    }

    const strippedChild = isInlineNode(child) ? stripInlineTargets(child) : stripBlockTargets(child);
    if (strippedChild !== child) {
      contentChanged = true;
    }

    return strippedChild;
  });

  const dataChanged = nextData !== node.data;
  if (!dataChanged && !contentChanged) {
    return node;
  }

  return {
    ...node,
    data: nextData,
    content: contentChanged ? nextContent : node.content,
  };
}

function stripDocumentTargets(doc: Document): Document {
  let contentChanged = false;

  const nextContent = doc.content.map(block => {
    const strippedBlock = stripTopLevelBlockTargets(block);
    if (strippedBlock !== block) {
      contentChanged = true;
    }

    return strippedBlock;
  });

  if (!contentChanged) {
    return doc;
  }

  return {
    ...doc,
    content: nextContent,
  };
}

interface CollectedTargets {
  inlineIds: string[];
  hyperlinkIds: string[];
  assetBlockIds: string[];
  assetHyperlinkIds: string[];
  byId: Map<string, unknown>;
}

/**
 * Depth-first traverse over the resolved document (document order). Collects inline
 * (`embedded-entry-inline`), entry-hyperlink, and embedded-block-asset ids and indexes
 * each node's resolved `data.target` by id so the shapers can read fields off it.
 */
function collectTargets(doc: Document): CollectedTargets {
  const inlineIds: string[] = [];
  const hyperlinkIds: string[] = [];
  const assetBlockIds: string[] = [];
  const assetHyperlinkIds: string[] = [];
  const byId = new Map<string, unknown>();

  function traverse(node: ContentfulNode | Document): void {
    if ('nodeType' in node) {
      const data = isRecord(node.data) ? node.data : undefined;
      const target = data?.target;
      const targetId = getSysId(target);

      if (typeof targetId === 'string' && !byId.has(targetId)) {
        byId.set(targetId, target);
      }

      if (node.nodeType === INLINES.EMBEDDED_ENTRY && typeof targetId === 'string') {
        inlineIds.push(targetId);
      }
      if (node.nodeType === INLINES.ENTRY_HYPERLINK && typeof targetId === 'string') {
        hyperlinkIds.push(targetId);
      }
      if (node.nodeType === BLOCKS.EMBEDDED_ASSET && typeof targetId === 'string') {
        assetBlockIds.push(targetId);
      }
      if (node.nodeType === INLINES.ASSET_HYPERLINK && typeof targetId === 'string') {
        assetHyperlinkIds.push(targetId);
      }
    }

    if ('content' in node && Array.isArray(node.content)) {
      node.content.forEach(child => traverse(child));
    }
  }

  traverse(doc);
  return { inlineIds, hyperlinkIds, assetBlockIds, assetHyperlinkIds, byId };
}

function getStringField(fields: Record<string, unknown>, key: string): string | undefined {
  const value = fields[key];
  return typeof value === 'string' ? value : undefined;
}

function getBooleanField(fields: Record<string, unknown>, key: string): boolean | undefined {
  const value = fields[key];
  return typeof value === 'boolean' ? value : undefined;
}

type IconPosition = 'before' | 'after';

function getIconPosition(fields: Record<string, unknown>): IconPosition | undefined {
  const value = fields.iconPosition;
  return value === 'before' || value === 'after' ? value : undefined;
}

type BreakBehavior = 'Desktop Only' | 'Mobile Only' | 'All';

function getBreakBehavior(fields: Record<string, unknown>): BreakBehavior | undefined {
  const value = fields.breakBehavior;
  return value === 'Desktop Only' || value === 'Mobile Only' || value === 'All' ? value : undefined;
}

function getInlineTextStyle(fields: Record<string, unknown>): InlineTextStyle | undefined {
  const value = fields.textStyle;
  return value === InlineTextStyle.ThemedColor || value === InlineTextStyle.UnlockedFont ? value : undefined;
}

type InlineEntry = NonNullable<
  NonNullable<NonNullable<DocumentWithEmbeddedEntries['links']>['entries']>['inline']
>[number];
type HyperlinkEntry = NonNullable<
  NonNullable<NonNullable<DocumentWithEmbeddedEntries['links']>['entries']>['hyperlink']
>[number];

/**
 * Recurse a nested rich-text field into a `DocumentWithEmbeddedEntries`. Depth is
 * capped at `MAX_NESTED_DEPTH`; beyond that (or for a non-document value) the field
 * is omitted, keeping the output finite and JSON-serializable on circular graphs.
 */
function recurseField(
  value: unknown,
  context: AdaptRichTextContext,
  depth: number,
): DocumentWithEmbeddedEntries | undefined {
  if (depth >= MAX_NESTED_DEPTH || !isRichTextDocument(value)) {
    return undefined;
  }
  return convert(value, context, depth + 1);
}

function shapeInlineEntry(
  contentTypeId: string,
  fields: Record<string, unknown>,
  id: string,
  contextKey: string | undefined,
  depth: number,
): InlineEntry | undefined {
  const typename = getTypenameForContentTypeId(contentTypeId);

  if (contentTypeId === 'disclosure') {
    const symbol = getStringField(fields, 'symbol');
    const copy = recurseField(fields.copy, { parentTypename: 'Disclosure', fieldName: 'copy' }, depth);
    return {
      __typename: typename,
      sys: { id },
      ...(symbol !== undefined && { symbol }),
      ...(copy !== undefined && { copy }),
    };
  }

  if (contentTypeId === 'modal') {
    const headline = getStringField(fields, 'headline');
    const disclaimerText = getStringField(fields, 'disclaimerText');
    const iconPosition = getIconPosition(fields);
    const iconOnlyLink = getBooleanField(fields, 'iconOnlyLink');
    const copy = recurseField(fields.copy, { parentTypename: 'Modal', fieldName: 'copy' }, depth);
    return {
      __typename: typename,
      sys: { id },
      ...(headline !== undefined && { headline }),
      ...(disclaimerText !== undefined && { disclaimerText }),
      ...(iconPosition !== undefined && { iconPosition }),
      ...(iconOnlyLink !== undefined && { iconOnlyLink }),
      ...(copy !== undefined && { copy }),
    };
  }

  if (contentTypeId === 'linebreak') {
    const breakBehavior = getBreakBehavior(fields);
    const testId = getStringField(fields, 'testId');
    return {
      __typename: typename,
      sys: { id },
      ...(breakBehavior !== undefined && { breakBehavior }),
      ...(testId !== undefined && { testId }),
    };
  }

  if (contentTypeId === 'inlineStyledText') {
    const textStyle = getInlineTextStyle(fields);
    const content = recurseField(fields.content, { parentTypename: 'InlineStyledText', fieldName: 'content' }, depth);
    return {
      __typename: typename,
      sys: { id },
      ...(textStyle !== undefined && { textStyle }),
      ...(content !== undefined && { content }),
    };
  }

  if (contentTypeId === 'shortcode') {
    // Shortcodes are only emitted in FULL contexts. In every Note context (and any
    // other non-FULL context) they are omitted entirely - render-identical to the
    // legacy sys-less `{ __typename: 'Shortcode' }`, which matches no consumer.
    if (!contextKey || !FULL_SHORTCODE_INLINE_CONTEXT_KEYS.has(contextKey)) {
      return undefined;
    }
    const shortCodeValue = fields.shortCodeValue;
    return {
      __typename: typename,
      sys: { id },
      ...(isRichTextDocument(shortCodeValue) && {
        shortCodeValue: { json: stripDocumentTargets(shortCodeValue) },
      }),
    };
  }

  return { __typename: typename, sys: { id } };
}

function shapeHyperlinkEntry(contentTypeId: string, fields: Record<string, unknown>, id: string): HyperlinkEntry {
  const typename = getTypenameForContentTypeId(contentTypeId);
  const slug = getStringField(fields, 'slug');
  const url = getStringField(fields, 'url');
  return {
    __typename: typename,
    sys: { id },
    ...(slug !== undefined && { slug }),
    ...(url !== undefined && { url }),
  };
}

function shapeAssets(ids: string[], byId: Map<string, unknown>): ContentfulAsset[] {
  const assets: ContentfulAsset[] = [];
  for (const id of Array.from(new Set(ids))) {
    const target = byId.get(id);
    if (isLink(target) || !isResolvedAsset(target)) {
      continue;
    }

    const asset = shapeAsset(target);
    if (asset) {
      assets.push(asset);
    }
  }
  return assets;
}

function convert(doc: Document, context: AdaptRichTextContext | undefined, depth: number): DocumentWithEmbeddedEntries {
  const { inlineIds, hyperlinkIds, assetBlockIds, assetHyperlinkIds, byId } = collectTargets(doc);
  const contextKey = context ? `${context.parentTypename}.${context.fieldName}` : undefined;

  const inline: InlineEntry[] = [];
  for (const id of Array.from(new Set(inlineIds))) {
    const target = byId.get(id);
    if (isLink(target) || !isResolvedEntry(target)) {
      continue;
    }
    const shaped = shapeInlineEntry(target.sys.contentType.sys.id, target.fields, id, contextKey, depth);
    if (shaped !== undefined) {
      inline.push(shaped);
    }
  }

  const entryHyperlink: HyperlinkEntry[] = [];
  for (const id of Array.from(new Set(hyperlinkIds))) {
    const target = byId.get(id);
    if (isLink(target) || !isResolvedEntry(target)) {
      continue;
    }
    entryHyperlink.push(shapeHyperlinkEntry(target.sys.contentType.sys.id, target.fields, id));
  }

  const assetKeys = contextKey ? RICH_TEXT_ASSET_LINK_KEYS[contextKey] : undefined;
  const block = assetKeys?.includes('block') ? shapeAssets(assetBlockIds, byId) : [];
  const assetHyperlink = assetKeys?.includes('hyperlink') ? shapeAssets(assetHyperlinkIds, byId) : [];
  const assets = {
    ...(block.length > 0 && { block }),
    ...(assetHyperlink.length > 0 && { hyperlink: assetHyperlink }),
  };

  return {
    json: stripDocumentTargets(doc),
    links: {
      entries: {
        inline,
        hyperlink: entryHyperlink,
      },
      ...(Object.keys(assets).length > 0 && { assets }),
    },
  };
}

/**
 * Converts a resolved REST `Document` (rich-text `data.target` resolved to full,
 * possibly circular entries) into the legacy `DocumentWithEmbeddedEntries`
 * (`{ json, links }`) shape consumed by `RichTextHeadline`/`RichTextCopy`.
 *
 * The emitted `json` has every embedded target stripped to `{ sys: { id } }`, and
 * `links.entries.inline` / `links.entries.hyperlink` are always present (deduped by
 * `sys.id`, document order). Output is always JSON-serializable.
 */
export function toDocumentWithEmbeddedEntries(
  doc: Document | DocumentWithEmbeddedEntries,
  context?: AdaptRichTextContext,
): DocumentWithEmbeddedEntries {
  return isRichTextDocument(doc) ? convert(doc, context, 0) : doc;
}
